// "Etiketler (EN)" — katalog çekmecesinde gösterge etiketinin İngilizce karşılığı (TODO-KPI-09).
// Kendi yükleme/kaydetme döngüsü vardır (GET/PUT/DELETE /kpi/metrics/:id/labels); MetricModal'ın
// ana formuna karışmaz — düğme `type="button"`, Enter ana formu tetiklemez. Yalnız mevcut metrikte
// görünür (yeni kayıtta önce metrik oluşturulur). Boş bırakıp kaydetmek çeviriyi siler.

import { useEffect, useState } from "react";
import type { KpiLang, KpiMetricLabel } from "@mahir/shared";
import {
  deleteKpiMetricLabel,
  listKpiMetricLabels,
  upsertKpiMetricLabel,
} from "../../api/kpi-metric-labels";
import { btnSmall, errorBox, fieldStyle, inputStyle, labelStyle } from "./kpi-form-styles";

export const METRIC_LABELS_COPY = {
  heading: "Etiketler (EN)",
  hint: "İngilizce çıktı (Excel ?lang=en) bu etiketi kullanır; boşsa Türkçe etiket kalır.",
  label: "İngilizce etiket",
  description: "İngilizce açıklama",
  save: "Etiketi kaydet",
  saving: "Kaydediliyor…",
  saved: "Kaydedildi",
  removed: "Çeviri kaldırıldı",
  failed: "Etiket kaydedilemedi",
} as const;

const LANG = "en" as const satisfies KpiLang;

export function MetricLabelsSection({
  metricId,
  canEdit,
}: {
  metricId: string;
  canEdit: boolean;
}): JSX.Element {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [loaded, setLoaded] = useState<KpiMetricLabel | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    let alive = true;
    setNote(null);
    void listKpiMetricLabels(metricId)
      .then((rows) => {
        if (!alive) return;
        const en = rows.find((r) => r.lang === LANG) ?? null;
        setLoaded(en);
        setLabel(en?.label ?? "");
        setDescription(en?.description ?? "");
      })
      .catch(() => {
        if (alive) setLoaded(null);
      });
    return () => {
      alive = false;
    };
  }, [metricId]);

  const dirty =
    label.trim() !== (loaded?.label ?? "") || description.trim() !== (loaded?.description ?? "");

  async function save(): Promise<void> {
    setBusy(true);
    setNote(null);
    try {
      if (label.trim() === "") {
        if (loaded) await deleteKpiMetricLabel(metricId, LANG);
        setLoaded(null);
        setDescription("");
        setNote({ kind: "ok", text: METRIC_LABELS_COPY.removed });
      } else {
        const row = await upsertKpiMetricLabel(metricId, {
          lang: LANG,
          label: label.trim(),
          description: description.trim() || null,
        });
        setLoaded(row);
        setNote({ kind: "ok", text: METRIC_LABELS_COPY.saved });
      }
    } catch (err) {
      setNote({
        kind: "err",
        text: err instanceof Error ? err.message : METRIC_LABELS_COPY.failed,
      });
    } finally {
      setBusy(false);
    }
  }

  const dis = !canEdit || busy;
  return (
    <fieldset
      style={{
        border: "1px solid var(--color-border)",
        borderRadius: 8,
        padding: "10px 12px",
        marginBottom: 12,
      }}
    >
      <legend style={{ ...labelStyle, padding: "0 4px" }}>{METRIC_LABELS_COPY.heading}</legend>
      <div className="text-xs mb-2" style={{ color: "var(--color-text-3)" }}>
        {METRIC_LABELS_COPY.hint}
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="m-label-en">
          {METRIC_LABELS_COPY.label}
        </label>
        <input
          id="m-label-en"
          style={inputStyle}
          value={label}
          disabled={dis}
          maxLength={200}
          lang="en"
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (dirty && !dis) void save();
            }
          }}
        />
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="m-desc-en">
          {METRIC_LABELS_COPY.description}
        </label>
        <textarea
          id="m-desc-en"
          rows={2}
          style={{ ...inputStyle, resize: "vertical" }}
          value={description}
          disabled={dis}
          maxLength={2000}
          lang="en"
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      {note ? (
        note.kind === "err" ? (
          <div style={{ ...errorBox, marginBottom: 8 }} role="alert">
            {note.text}
          </div>
        ) : (
          <div role="status" className="text-xs mb-2" style={{ color: "var(--color-success)" }}>
            {note.text}
          </div>
        )
      ) : null}
      {canEdit ? (
        <button
          type="button"
          style={{ ...btnSmall, opacity: dirty && !busy ? 1 : 0.5 }}
          disabled={!dirty || busy}
          aria-busy={busy || undefined}
          onClick={() => void save()}
        >
          {busy ? METRIC_LABELS_COPY.saving : METRIC_LABELS_COPY.save}
        </button>
      ) : null}
    </fieldset>
  );
}
